import { DrugConfirmation, Prescription, DrugIcd } from "@fusion-advantage/backcompat"
import _ from 'lodash'

// TODO [ISSUE-1314] Refactor DrugIcdCodeDataSet
// Without digging in too deep, I suspect that some of these items should be
// located directly on DrugSliceState and loaded in at form load time,
// and others should be provided by a reselect selector based on other state
// in the form--  instead of being persisted by a component async effect.
// If it's not an easy thing to figure out, we can collaborate as needed.

export type DrugIcdCodeDataSet = {
	allIcdCodes: DrugIcd[],
	supportedCodes: string[],
	priorCodes: string[],
	icdCodeMap: DrugIcdCodeMap,
}
export interface DrugIcdCodeMap {
	[key: string]: DrugIcd
}

export const defaultDrugIcdDataSet = (): DrugIcdCodeDataSet => ({
	allIcdCodes: [],
	supportedCodes: [],
	priorCodes: [],
	icdCodeMap: {},
})

export const generateDrugIcdDataSet = (allIcds: DrugIcd[], priorIcds: string[], formConceptIcds: string[]): DrugIcdCodeDataSet => {
	const icdCodeMap = allIcds.reduce<DrugIcdCodeMap>((map, code) => {
		map[code.Icd10Code] = code

		return map
	}, {})

	return {
		allIcdCodes: allIcds,
		priorCodes: priorIcds,
		supportedCodes: formConceptIcds,
		icdCodeMap
	}
}

export enum MedReconItemNotInUseReason {
	Allergy = 'Allergy',
	ConditionResolved = 'Condition resolved',
	NotEffective = 'Not effective',
	PrescriberDirection = 'Prescriber direction',
	SideEffects = 'Side effects',
	TooExpensive = 'Too expensive',
	Other = 'Other',
	Error = 'Error',
}

export const medReconItemIsReconciled = (model: Prescription) => {
	if(model.InUse === null) return false
	if(model.InUse === true && model.Indications.length < 1) return false
	if(model.InUse === false && model.Reason === null) return false
	if(medReconItemMustConfirm(model)) return false

	return true
}

export const medReconItemMustConfirm = (model: Prescription) => {
	return model.IsPrior &&
		model.Confirmation === null &&
		model.InUse === true
}

export const medReconItemCanSelectReason = (model: Prescription) => {
	return model.InUse === false
}

export const medReconItemCanAddIndication = (model: Prescription): boolean => {
	if(
		model.InUse == null ||
		model.Reason == MedReconItemNotInUseReason.ConditionResolved ||
		model.Reason == MedReconItemNotInUseReason.Error
	) {
		return false
	}
	return true
}

// can only remove non-claims rx's
export const medReconItemCanRemove = (model: Prescription) => {
	return model.IsPrior === false
}

// can only reset rx from claims history
export const medReconItemCanReset = (model: Prescription) => {
	return model.IsPrior === true && (
		model.InUse !== null ||
		model.Confirmation !== null ||
		model.Indications?.length > 0
	)
}

export const mergeManualDrugs = (existing: Prescription[], newOrChangedModels: Prescription[]): Prescription[] => {
	let existingModels = _.cloneDeep(existing)
	const existingModelIds = existing.map(x => x.Product.ID)
	const updateMap = _.keyBy(newOrChangedModels, 'Product.ID')
	const newModels = newOrChangedModels.filter(x => existingModelIds.includes(x.Product.ID) === false)

	_.forEach(existingModels, (existingModel) => {
		if(updateMap[existingModel.Product.ID]) {
			existingModel.Confirmation = DrugConfirmation.ManualEntry
			existingModel.InUse = true
		}
	})

	return [...existingModels, ...newModels]
}

/*
The table will sort automatically on load and upon any change. (Tech words: this is just a selector on the table state that automatically sorts the rows inbound to the control; nothing imperative!)
ALL unreconciled drugs will be displayed together in an alphabetized group above the reconciled drugs.
ALL reconciled drugs will be displayed together in an alphabetized group below the unreconciled drugs.
No changes to any other attributes, besides whether a drug is fully reconciled or not, will affect the sort order of the table.
The net effect of this is that on form load, all drugs will be unreconciled priors, shown alphabetically.  When the clinician is done with reconciliation, all drugs will be reconciled, shown alphabetically.
*/
export const sortedPrescriptionModels = (models: Prescription[]): Prescription[] => {
	if(models.length === 0) return []

	const unreconciledRows: Prescription[] = []
	const reconciledRows: Prescription[] = []

	_.forEach(models, (model, idx) => {
		const reconciled = medReconItemIsReconciled(model)

		if(reconciled === true) reconciledRows.push(model)
		else unreconciledRows.push(model)
	})

	_.forEach([unreconciledRows, reconciledRows], (arr) => {
		arr.sort((drugA, drugB) => {
			if(drugA.Product.Name < drugB.Product.Name) return -1
			if(drugA.Product.Name > drugB.Product.Name) return 1

			return 0
		})
	})

	return [...unreconciledRows, ...reconciledRows]
}

export const MockDrugIcdCodes: DrugIcd[] = [
	{
		Icd10Code: "I1001",
		Description: "Mock condition 1",
		HistoryOf: null,
		ComorbidityStatus: null,
	},
	{
		Icd10Code: "I1002",
		Description: "Mock condition 2",
		HistoryOf: "Previous condition A",
		ComorbidityStatus: null,
	},
	{
		Icd10Code: "I1003",
		Description: "Mock condition 3",
		HistoryOf: null,
		ComorbidityStatus: null,
	},
	{
		Icd10Code: "I1004",
		Description: "Mock condition 4",
		HistoryOf: "Previous condition B",
		ComorbidityStatus: null,
	},
	{
		Icd10Code: "I1005",
		Description: "Mock condition 5",
		HistoryOf: null,
		ComorbidityStatus: null,
	},
	{
		Icd10Code: "I1006",
		Description: "Mock condition 6",
		HistoryOf: "Previous condition C",
		ComorbidityStatus: null,
	},
	{
		Icd10Code: "I1007",
		Description: "Mock condition 7",
		HistoryOf: null,
		ComorbidityStatus: null,
	},
	{
		Icd10Code: "I1008",
		Description: "Mock condition 8",
		HistoryOf: "Previous condition D",
		ComorbidityStatus: null,
	},
	{
		Icd10Code: "I1009",
		Description: "Mock condition 9",
		HistoryOf: null,
		ComorbidityStatus: null,
	},
	{
		Icd10Code: "I1010",
		Description: "Mock condition 10",
		HistoryOf: "Previous condition E",
		ComorbidityStatus: null,
	},
	{
		Icd10Code: "I1011",
		Description: "Mock condition 11",
		HistoryOf: null,
		ComorbidityStatus: null,
	},
	{
		Icd10Code: "I1012",
		Description: "Mock condition 12",
		HistoryOf: "Previous condition F",
		ComorbidityStatus: null,
	},
	{
		Icd10Code: "I1013",
		Description: "Mock condition 13",
		HistoryOf: null,
		ComorbidityStatus: null,
	},
	{
		Icd10Code: "I1014",
		Description: "Mock condition 14",
		HistoryOf: "Previous condition G",
		ComorbidityStatus: null,
	},
	{
		Icd10Code: "I1015",
		Description: "Mock condition 15",
		HistoryOf: null,
		ComorbidityStatus: null,
	},
	{
		Icd10Code: "I1016",
		Description: "Mock condition 16",
		HistoryOf: "Previous condition H",
		ComorbidityStatus: null,
	},
	{
		Icd10Code: "I1017",
		Description: "Mock condition 17",
		HistoryOf: null,
		ComorbidityStatus: null,
	},
	{
		Icd10Code: "I1018",
		Description: "Mock condition 18",
		HistoryOf: "Previous condition I",
		ComorbidityStatus: null,
	},
	{
		Icd10Code: "I1019",
		Description: "Mock condition 19",
		HistoryOf: null,
		ComorbidityStatus: null,
	},
	{
		Icd10Code: "I1020",
		Description: "Mock condition 20",
		HistoryOf: "Previous condition J",
		ComorbidityStatus: null,
	}
];

export const MedicationReconciliationMockModels: Prescription[] = [
	{
		Product: {
			ID: 1,
			Name: 'Dondaplatz',
		},
		Indications : [],
		InUse: null,
		IsPrior: true,
		Confirmation: null,
		Reason: null,
	},
	{
		Product: {
			ID: 2,
			Name: 'Wexatrancillin',
		},
		Indications : [],
		InUse: null,
		IsPrior: false,
		Confirmation: null,
		Reason: null,
	},
	{
		Product: {
			ID: 3,
			Name: 'Plaxodil',
		},
		Indications : [],
		InUse: true,
		IsPrior: false,
		Confirmation: DrugConfirmation.ManualEntry,
		Reason: null,
	},
	{
		Product: {
			ID: 4,
			Name: 'Charmycon',
		},
		Indications : [],
		InUse: true,
		IsPrior: false,
		Confirmation: DrugConfirmation.Scan,
		Reason: null,
	},
	{
		Product: {
			ID: 5,
			Name: 'Somereallylongmedicationnamethatdoesntexistbutiscooltolookat',
		},
		Indications : [],
		InUse: false,
		IsPrior: false,
		Confirmation: null,
		Reason: null,
	},
	{
		Product: {
			ID: 6,
			Name: 'Fake drug 1',
		},
		Indications : [],
		InUse: false,
		IsPrior: false,
		Confirmation: DrugConfirmation.ManualEntry,
		Reason: null,
	},
	{
		Product: {
			ID: 7,
			Name: 'Fake drug 2',
		},
		Indications : [],
		InUse: false,
		IsPrior: false,
		Confirmation: DrugConfirmation.Scan,
		Reason: null,
	},
	{
		Product: {
			ID: 8,
			Name: 'Fake drug 3',
		},
		Indications : [],
		InUse: true,
		IsPrior: false,
		Confirmation: DrugConfirmation.Scan,
		Reason: null,
	},
	{
		Product: {
			ID: 9,
			Name: 'Fake drug 4',
		},
		Indications : [],
		InUse: true,
		IsPrior: true,
		Confirmation: DrugConfirmation.Scan,
		Reason: null,
	},
	{
		Product: {
			ID: 10,
			Name: 'Medixafen',
		},
		Indications : [],
		InUse: false,
		IsPrior: true,
		Confirmation: DrugConfirmation.ManualEntry,
		Reason: null,
	},
	{
		Product: {
			ID: 11,
			Name: 'Thermoxel',
		},
		Indications : [],
		InUse: true,
		IsPrior: false,
		Confirmation: DrugConfirmation.Scan,
		Reason: null,
	},
	{
		Product: {
			ID: 12,
			Name: 'Zyphorol',
		},
		Indications : [],
		InUse: false,
		IsPrior: false,
		Confirmation: null,
		Reason: null,
	},
	{
		Product: {
			ID: 13,
			Name: 'Neuroflax',
		},
		Indications : [],
		InUse: true,
		IsPrior: true,
		Confirmation: DrugConfirmation.ManualEntry,
		Reason: null,
	},
	{
		Product: {
			ID: 14,
			Name: 'Cardiotrex',
		},
		Indications : [],
		InUse: false,
		IsPrior: true,
		Confirmation: null,
		Reason: null,
	},
	{
		Product: {
			ID: 15,
			Name: 'Allercept',
		},
		Indications : [],
		InUse: true,
		IsPrior: false,
		Confirmation: DrugConfirmation.Scan,
		Reason: null,
	},
	{
		Product: {
			ID: 16,
			Name: 'Coughnix',
		},
		Indications : [],
		InUse: false,
		IsPrior: false,
		Confirmation: DrugConfirmation.ManualEntry,
		Reason: null,
	},
	{
		Product: {
			ID: 17,
			Name: 'Bronchomax',
		},
		Indications : [],
		InUse: true,
		IsPrior: false,
		Confirmation: DrugConfirmation.Scan,
		Reason: null,
	},
	{
		Product: {
			ID: 18,
			Name: 'Gastrolit',
		},
		Indications : [],
		InUse: false,
		IsPrior: true,
		Confirmation: null,
		Reason: null,
	}
];

export const MassiveMockDrugIcdDataSet: DrugIcdCodeDataSet = generateDrugIcdDataSet(
	Array.from({ length: 95000 }, (_, i) => 
	({ 
		Icd10Code: `Mock IcdCode ${i + 1}`,
		Description: `Mock Description ${i + 1}`,
		HistoryOf: null,
		ComorbidityStatus: null,
	})),
	[
		"Mock IcdCode 1",
		"Mock IcdCode 2",
	],
	[
		"Mock IcdCode 45",
		"Mock IcdCode 46",
	]
)

export const MockDrugIcdDataSet: DrugIcdCodeDataSet = generateDrugIcdDataSet(
	MockDrugIcdCodes,
	[
		MockDrugIcdCodes[3].Icd10Code,
		MockDrugIcdCodes[12].Icd10Code,
		MockDrugIcdCodes[6].Icd10Code,
		MockDrugIcdCodes[1].Icd10Code,
	],
	[
		MockDrugIcdCodes[2].Icd10Code,
		MockDrugIcdCodes[3].Icd10Code,
		MockDrugIcdCodes[6].Icd10Code,
		MockDrugIcdCodes[7].Icd10Code,
		MockDrugIcdCodes[8].Icd10Code,
		MockDrugIcdCodes[10].Icd10Code,
		MockDrugIcdCodes[12].Icd10Code,
		MockDrugIcdCodes[13].Icd10Code,
		MockDrugIcdCodes[14].Icd10Code,
		MockDrugIcdCodes[15].Icd10Code,
		MockDrugIcdCodes[16].Icd10Code,
		MockDrugIcdCodes[18].Icd10Code,
		MockDrugIcdCodes[19].Icd10Code,
	]
)